import { getAssetRelatedDtoIds } from '@/entities/assets/helpers/getAssetRelatedDtoIds';
import { getAssetRelatedFilePaths } from '@/entities/assets/helpers/getAssetRelatedFilepaths';
import { isContactDto } from '@/entities/contact';
import { isPersonalIdentifierDocumentDto, isPrivateDocumentDto } from '@/entities/document';
import { getDocumentFilepaths } from '@/entities/document/helpers/getDocumentsFilepaths';
import { isObject } from '@/shared/utils/guards';
import { isDtoId } from '@/shared/utils/is';

import { getContactFilepaths } from '../../sj-orm';
import { CollectionName } from '../../sj-orm/constants';
import { SJDatabase } from '../../sj-orm/core';
import { DtoId } from '../../sj-orm/models/base.dto';

const getUniqArr = <T>(arr: T[]): T[] => Array.from(new Set(arr));

type DtoIdsList = DtoId[];

const getGroupByCollectionDtoIds = (dtoIds: DtoIdsList): Record<CollectionName, string[]> => {
  return dtoIds.reduce<Record<CollectionName, string[]>>(
    (byCollections, dtoId) => {
      const collectionName = isDtoId(dtoId);

      if (!collectionName) {
        return byCollections;
      }

      if (!byCollections[collectionName]) {
        byCollections[collectionName] = [];
      }

      if (!byCollections[collectionName].includes(dtoId)) {
        byCollections[collectionName].push(dtoId);
      }

      return byCollections;
    },
    {} as Record<CollectionName, string[]>,
  );
};

const ALLOWED_COLLECTIONS = [
  CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
  CollectionName.PRIVATE_DOCUMENTS,
  CollectionName.CONTACTS,
  CollectionName.ENCRYPTED_FILE_KEY,
  CollectionName.FILE_META_INFO,
  CollectionName.ASSETS,
];
type GetRelatedToAssetDtosIdsProps = {
  assetDtoId: string;
  sjDb: SJDatabase;
  includeAsset?: boolean;
};

export const getRelatedToAssetDtosIds = ({
  assetDtoId,
  sjDb,
  includeAsset = false,
}: GetRelatedToAssetDtosIdsProps) => {
  const assetDto = sjDb.collections.assets?.findOne((dto) => dto.id === assetDtoId);

  if (!isObject(assetDto)) {
    throw new Error('Asset not found');
  }

  const dtoIds = getAssetRelatedDtoIds(assetDto);
  let filePaths = getAssetRelatedFilePaths(assetDto);

  //files from contacts/documents/documentIds
  dtoIds.forEach((dtoId) => {
    const dtoCollection = isDtoId(dtoId);

    if (!dtoCollection || !ALLOWED_COLLECTIONS.includes(dtoCollection)) {
      return;
    }

    const dto = sjDb.collections[dtoCollection]?.findOne((item) => item.id === dtoId);

    if (!isObject(dto)) {
      return;
    }

    // Get only filepaths since we don't need nested entities except files
    if (isContactDto(dto)) {
      filePaths = filePaths.concat(getContactFilepaths(dto));
    }

    if (isPersonalIdentifierDocumentDto(dto)) {
      filePaths = filePaths.concat(getDocumentFilepaths(dto));
    }

    if (isPrivateDocumentDto(dto)) {
      filePaths = filePaths.concat(getDocumentFilepaths(dto));
    }
  });

  const fileMetaInfoDtoIds =
    sjDb.collections.fileMetaInfos
      ?.findMany((item) => filePaths.includes(item.filePath))
      .map((fileMetaInfo) => fileMetaInfo.id) ?? [];

  const encryptedFileKeysDtoIds =
    sjDb.collections.encryptedFileKey
      ?.findMany((encFileKey) => filePaths.some((filePath) => encFileKey.id.includes(filePath)))
      .map((encFileKey) => encFileKey.id) ?? [];

  const dtoIdsByCollections = getGroupByCollectionDtoIds(dtoIds);

  if (encryptedFileKeysDtoIds.length) {
    dtoIdsByCollections[CollectionName.ENCRYPTED_FILE_KEY] = getUniqArr(encryptedFileKeysDtoIds);
  }

  if (fileMetaInfoDtoIds.length) {
    dtoIdsByCollections[CollectionName.FILE_META_INFO] = getUniqArr(fileMetaInfoDtoIds);
  }

  if (includeAsset) {
    dtoIdsByCollections[CollectionName.ASSETS] = [assetDtoId];
  }

  const result = new Map<CollectionName, string[]>();

  Object.keys(dtoIdsByCollections).forEach((key) => {
    const collectionName = key as CollectionName;
    if (!ALLOWED_COLLECTIONS.includes(collectionName)) {
      return;
    }

    result.set(collectionName, getUniqArr(dtoIdsByCollections[collectionName]));
  });

  return result;
};
